import { useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/hooks/use-user';
import { Loader2 } from 'lucide-react';
import { JoinCreateView } from '@/components/dashboard/join-create-view';
import { applyThemeColors } from '@/lib/brand-theme';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
import { resolveCommunityLandingPath } from '@/lib/community-navigation';
import { resolvePreferredCommunity } from '@/lib/last-active-community';
import { useTenant } from '@/providers/tenant-context';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user, communities, isLoading, isError } = useUser();

  useLayoutEffect(() => {
    applyThemeColors(tenant?.themeColors);
    notifyNativeSafeAreaTheme();
  }, [tenant?.themeColors]);

  useEffect(() => {
    if (!isLoading && !isError && user && communities.length > 0) {
      const preferredCommunity = resolvePreferredCommunity(communities, user.id);
      if (preferredCommunity) {
        navigate(resolveCommunityLandingPath(preferredCommunity, user), { replace: true });
      }
    } else if (isError) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isError, user, communities, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Auth Failed handled in effect
  if (!user) return null;

  // No communities: Show onboarding (header is rendered inside JoinCreateView)
  if (communities.length === 0) {
    return <JoinCreateView />;
  }

  // Redirecting...
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
